/* eslint-disable no-undef */
import React, { useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import classes from './DrawerMenu.module.scss';
import { setSessionId } from '../../store/actions/general';
import { setMessages, setMode } from '../../store/actions/messages';
import { setResetChat, setDrawerIsOpen, setEndChat, setAlertIsOpen } from '../../store/actions/general';
import PopOver from '../controls/PopOver/PopOver';
import MuiPlaceholder from '../controls/MuiPlaceholder/MuiPlaceholder';

const DrawerMenu = ({socket}) => {
  const { drawerIsOpen, muiFontLoaded, sessionId, endChat, fingerprintId } = useSelector(state => state.general);
  const isMounted = useRef(true);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    }
  }, [])

  const handleOpen = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setDrawerIsOpen(drawerIsOpen ? false : true));
  }, [dispatch, drawerIsOpen]);

  const handleClose = useCallback((e) => {
    if (isMounted.current) {
      dispatch(setDrawerIsOpen(false));
    }
  }, [dispatch]);

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${day}-${month}`;
  }

  async function downloadFile(blob, fileName) {
    try {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.style.display = "none"; // Hide the anchor
      document.body.appendChild(a); // Add the anchor to the DOM
      a.click(); // Simulate a click to trigger the download

      // Clean up
      URL.revokeObjectURL(a.href);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  }

  const downloadTranscript = useCallback((e) => {
    const configHeaders = {
      'Content-Type': 'application/json',
      'bot-config': window.sutherland_variables.botId
    }

    const urlParams = new URLSearchParams(window.location.search);
    let cacheDebug = JSON.parse(urlParams.get('enable_debug'));

    if (cacheDebug) {
      configHeaders['bypass-cache'] = "true";
    }

    fetch(`${window.sutherland_variables.engineBaseUrl}/api/transcript/download/${sessionId}`, {
      method: 'GET',
      headers: configHeaders
    })
    .then(async response => {
      downloadFile(await response.blob(), `Transcript_${formatDate(new Date())}.pdf`);
    })
    .catch(err => {
      console.error(err);
    });
  },[sessionId])

  const restartChat = useCallback(() => {
    dispatch(setDrawerIsOpen(false))
    if (!endChat) {
      setTimeout(() => {
        socket.emit("send", {
          name: "restart-chat-event",
          parameters:
          {
            FingerprintId: fingerprintId, 
            UserAgent: window.sutherland_variables.user_agent,
            Browser: window.sutherland_variables.browser,
            DeviceCategory: window.sutherland_variables.device,
            ScreenResolution: `${window.screen.width}x${window.screen.height}`,
            VisitorIP: window.sutherland_variables.ip_address,
            url: document.URL,
            title: document.title
          }
        });
        dispatch(setEndChat(false))
      }, 1000);
    } else {
      dispatch(setResetChat(true))
      dispatch(setEndChat(false))
      dispatch(setMode("bot"))
      dispatch(setSessionId(''))
      dispatch(setMessages([]))
      dispatch(setZendeskSession(false))
      dispatch(setAlertIsOpen(true))
      setTimeout(() => {
        socket.emit("reset", {});
      }, 1000);
    }
  }, [dispatch, socket, endChat, fingerprintId]);

  const faqsHandler = useCallback(() => {
    dispatch(setDrawerIsOpen(false))
    setTimeout(() => {
      socket.emit("send", { name: "popular-faqs-event", parameters:{} });
    }, 1000);
  }, [dispatch, socket]);

  return (
    <div className={classes.DrawerMenu}>
      <div className={classes.DrawerMenuSVG} onClick={handleOpen}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path fillRule="evenodd" clipRule="evenodd" d="M13.5 12C13.5 12.8041 12.8284 13.4559 12 13.4559C11.1716 13.4559 10.5 12.8041 10.5 12C10.5 11.1959 11.1716 10.5441 12 10.5441C12.8284 10.5441 13.5 11.1959 13.5 12ZM13.5 5.20588C13.5 6.00994 12.8284 6.66176 12 6.66176C11.1716 6.66176 10.5 6.00994 10.5 5.20588C10.5 4.40182 11.1716 3.75 12 3.75C12.8284 3.75 13.5 4.40182 13.5 5.20588ZM13.5 18.7941C13.5 19.5982 12.8284 20.25 12 20.25C11.1716 20.25 10.5 19.5982 10.5 18.7941C10.5 17.9901 11.1716 17.3382 12 17.3382C12.8284 17.3382 13.5 17.9901 13.5 18.7941Z" fill="white"
          style={{fill: window.sutherland_variables.ui.header_styles.icon_colors}}
          />
        </svg>
      </div>
      <PopOver close={handleClose} opened={drawerIsOpen}>
        <ul className={classes.MenuBox}>
          <li className={classes.MenuItem} onClick={downloadTranscript}>
            <div className={classes.MenuItemIcon}>
              <MuiPlaceholder
                isMuiFontLoaded={muiFontLoaded}
                element={<span aria-hidden="true">download_file</span>}
                width={0}
                height={0}
                backgroundColor={'transparent'} />
            </div>
            <div className="text">
              <Trans i18nKey="drawerMenu.downloadChat">
                Download Conversation
              </Trans>
            </div>
          </li>
          <li className={classes.MenuItem} onClick={restartChat}>
            <div className={classes.MenuItemIcon}>
              <MuiPlaceholder
                  isMuiFontLoaded={muiFontLoaded}
                  element={<span aria-hidden="true">restart_alt</span>}
                  width={0}
                  height={0}
                  backgroundColor={'transparent'} />
            </div>
            <div className="text">
              <Trans i18nKey="drawerMenu.restartChat">
                Restart Chat
              </Trans>
            </div>
          </li>
          <li className={classes.MenuItem} onClick={faqsHandler}>
            <div className={classes.MenuItemIcon}>
              <MuiPlaceholder
                  isMuiFontLoaded={muiFontLoaded}
                  element={<span aria-hidden="true">info_outlined</span>}
                  width={0}
                  height={0}
                  backgroundColor={'transparent'} />
            </div>
            <div className="text">
              <Trans i18nKey="drawerMenu.popularFaqs">
                Popular FAQs
              </Trans>
            </div>
          </li>
        </ul>
      </PopOver>
    </div>
  );
};
export default DrawerMenu;
